// src/products/dataroom/pages/DataroomUserActivityPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Activity, Filter, Download, RefreshCw, User, Building, FileText, Eye, Download as DownloadIcon, FolderOpen, LogIn } from 'lucide-react';
import { supabase } from '../../../config';
import { formatDateTime } from '../utils/dateUtils';
import { getUserDisplayName } from '../../../services/utilityService';

const DataroomUserActivityPage = ({ projectId, projectName, userRole }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState('all');
  const [selectedAction, setSelectedAction] = useState('all');
  const [dateRange, setDateRange] = useState('7'); // '1', '7', '30', 'all'
  const [users, setUsers] = useState({});
  const [projectUsers, setProjectUsers] = useState([]);

  const loadProjectUsers = useCallback(async () => {
    try {
      // First, get dataroom users
      const { data: dataroomUsers, error: dataroomError } = await supabase
        .from('dataroom_users')
        .select('user_id')
        .eq('project_id', projectId)
        .eq('status', 'active');

      if (dataroomError) throw dataroomError;

      if (!dataroomUsers || dataroomUsers.length === 0) {
        setUsers({});
        setProjectUsers([]);
        return;
      }

      // Get unique user IDs
      const userIds = [...new Set(dataroomUsers.map(item => item.user_id))];

      // Fetch user profiles for these users
      const { data: userProfiles, error: profilesError } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, email, username')
        .in('id', userIds);

      if (profilesError) throw profilesError;

      // Create user map and list
      const userMap = {};
      const userList = [];
      
      if (userProfiles) {
        userProfiles.forEach(user => {
          userMap[user.id] = getUserDisplayName(user, 'Unknown');
          userList.push({
            id: user.id,
            name: getUserDisplayName(user, 'Unknown'),
            email: user.email
          });
        });
      }

      setUsers(userMap);
      setProjectUsers(userList);
    } catch (error) {
      console.error('Error loading project users:', error);
    }
  }, [projectId]);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      // Build base query for dataroom_logs
      let query = supabase
        .from('dataroom_logs')
        .select('*')
        .eq('project_id', projectId)
        .order('timestamp', { ascending: false })
        .limit(200);

      // Apply date filter
      if (dateRange !== 'all') {
        const days = parseInt(dateRange);
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - days);
        query = query.gte('timestamp', startDate.toISOString());
      }

      // Apply user filter
      if (selectedUser !== 'all') {
        query = query.eq('user_id', selectedUser);
      }

      // Apply action filter
      if (selectedAction !== 'all') {
        query = query.eq('action', selectedAction);
      }

      const { data: logs, error: logsError } = await query;

      if (logsError) throw logsError;

      if (!logs || logs.length === 0) {
        setLogs([]);
        return;
      }

      // Get unique user IDs from logs
      const userIds = [...new Set(logs.map(log => log.user_id))];

      // Fetch user profiles for these users
      const { data: userProfiles, error: profilesError } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, email, username')
        .in('id', userIds);

      if (profilesError) throw profilesError;

      // Create a map of user profiles
      const userMap = {};
      if (userProfiles) {
        userProfiles.forEach(user => {
          userMap[user.id] = user;
        });
      }

      // Combine logs with user data
      const logsWithUsers = logs.map(log => ({
        ...log,
        user_profiles: userMap[log.user_id] || null
      }));

      setLogs(logsWithUsers);
    } catch (error) {
      console.error('Error loading logs:', error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, dateRange, selectedUser, selectedAction]);

  useEffect(() => {
    loadProjectUsers();
    loadLogs();
  }, [loadProjectUsers, loadLogs]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return formatDateTime(timestamp);
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'login':
      case 'project_accessed':
        return <LogIn className="w-4 h-4" />;
      case 'file_viewed':
      case 'file_previewed':
        return <Eye className="w-4 h-4" />;
      case 'file_downloaded':
        return <DownloadIcon className="w-4 h-4" />;
      case 'folder_accessed':
        return <FolderOpen className="w-4 h-4" />;
      case 'file_uploaded':
      case 'folder_created':
        return <FileText className="w-4 h-4" />;
      case 'user_added':
      case 'user_removed':
        return <User className="w-4 h-4" />;
      case 'project_created':
      case 'project_updated':
        return <Building className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'login':
      case 'project_accessed':
        return 'text-green-600 bg-green-50';
      case 'file_viewed':
      case 'file_previewed':
        return 'text-purple-600 bg-purple-50';
      case 'file_downloaded':
        return 'text-orange-600 bg-orange-50';
      case 'folder_accessed':
        return 'text-blue-600 bg-blue-50';
      case 'file_uploaded':
      case 'folder_created':
        return 'text-indigo-600 bg-indigo-50';
      case 'user_added':
      case 'user_removed':
        return 'text-pink-600 bg-pink-50';
      case 'project_created':
      case 'project_updated':
        return 'text-gray-600 bg-gray-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getActionDescription = (log) => {
    const userName = users[log.user_id] || getUserDisplayName(log.user_profiles || {}, 'Unknown User');
    
    switch (log.action) {
      case 'login':
        return `${userName} logged into the dataroom`;
      case 'project_accessed':
        return `${userName} accessed the dataroom`;
      case 'file_viewed':
        return `${userName} viewed ${log.details?.file_name || 'a file'}`;
      case 'file_downloaded':
        return `${userName} downloaded ${log.details?.file_name || 'a file'}`;
      case 'folder_accessed':
        return `${userName} accessed folder: ${log.details?.folder_name || 'Unknown Folder'}`;
      case 'file_uploaded':
        return `${userName} uploaded ${log.details?.file_name || 'a file'}`;
      case 'folder_created':
        return `${userName} created folder: ${log.details?.folder_name || 'Unknown Folder'}`;
      case 'user_added':
        return `${userName} added a user to the project`;
      case 'user_removed':
        return `${userName} removed a user from the project`;
      case 'project_created':
        return `${userName} created the project`;
      case 'project_updated':
        return `${userName} updated project settings`;
      default:
        return `${userName} performed ${log.action.replace('_', ' ')}`;
    }
  };

  const exportLogs = () => {
    const csv = [
      ['Timestamp', 'User', 'Action', 'Description', 'Details'].join(','),
      ...logs.map(log => [
        formatTimestamp(log.timestamp),
        users[log.user_id] || getUserDisplayName(log.user_profiles || {}, 'Unknown'),
        log.action || 'N/A',
        getActionDescription(log),
        JSON.stringify(log.details || {}).replace(/"/g, '""')
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dataroom-activity-logs-${projectName || 'project'}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Activity</h1>
          <p className="text-sm text-gray-600 mt-1">
            Activity logs for {projectName || 'this dataroom'}
          </p>
        </div>
        <div className="flex gap-2">
          {logs.length > 0 && (
            <button
              onClick={exportLogs}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          )}
          <button
            onClick={loadLogs}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 bg-white p-3 rounded-lg shadow">
        <Filter className="w-4 h-4 text-gray-500" />
        <div className="flex gap-2 flex-wrap">
          {/* User Filter */}
          <select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            className="px-2 py-1 border border-gray-300 rounded text-xs"
          >
            <option value="all">All Users</option>
            {projectUsers.map(user => (
              <option key={user.id} value={user.id}>
                {user.name}
              </option>
            ))}
          </select>

          {/* Action Filter */}
          <select
            value={selectedAction}
            onChange={(e) => setSelectedAction(e.target.value)}
            className="px-2 py-1 border border-gray-300 rounded text-xs"
          >
            <option value="all">All Actions</option>
            <option value="login">Logins</option>
            <option value="project_accessed">Project Access</option>
            <option value="file_viewed">File Views</option>
            <option value="file_downloaded">Downloads</option>
            <option value="folder_accessed">Folder Access</option>
            <option value="file_uploaded">File Uploads</option>
            <option value="folder_created">Folder Creation</option>
            <option value="user_added">User Management</option>
          </select>

          {/* Date Range Filter */}
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-2 py-1 border border-gray-300 rounded text-xs"
          >
            <option value="1">Last 24 hours</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="all">All time</option>
          </select>
        </div>
      </div>


      {/* Logs Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600 text-sm">Loading activity logs...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No activity logs found</p>
            <p className="text-xs mt-1">
              Activity will appear here as users interact with the dataroom
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8">
                    Icon
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Details
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                    More
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className={`p-1 rounded ${getActionColor(log.action)}`}>
                        {getActionIcon(log.action)}
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {users[log.user_id] || getUserDisplayName(log.user_profiles || {}, 'Unknown')}
                      </div>
                      <div className="text-xs text-gray-500">
                        {log.user_profiles?.email || 'No email'}
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {log.action.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-sm text-gray-900 max-w-xs truncate">
                        {getActionDescription(log).replace(/^[^:]+: /, '')}
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="text-sm text-gray-600">
                        {formatTimestamp(log.timestamp)}
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {log.details && Object.keys(log.details).length > 0 && (
                        <details className="relative cursor-pointer">
                          <summary className="text-xs text-blue-600 hover:text-blue-800">
                            Details
                          </summary>
                          <div className="absolute right-0 z-10 mt-1 bg-white border border-gray-200 rounded shadow-lg p-2 max-w-md">
                            <pre className="text-xs bg-gray-50 p-2 rounded overflow-auto max-h-32">
                              {JSON.stringify(log.details, null, 2)}
                            </pre>
                          </div>
                        </details>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
};

export default DataroomUserActivityPage;
